<template>
  <el-dialog
    :model-value="modelValue"
    @update:model-value="$emit('update:modelValue', $event)"
    :title="isEditMode ? '작업 수정' : '작업 추가'"
    width="600px"
    destroy-on-close
  >
    <el-form
      ref="formRef"
      :model="form"
      :rules="rules"
      label-width="100px"
      v-loading="loading"
    >
      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="작업유형" prop="work_type">
            <el-select v-model="form.work_type" placeholder="작업유형 선택">
              <el-option label="패턴" value="PATTERN" />
              <el-option label="재단" value="CUTTING" />
              <el-option label="봉제" value="SEWING" />
              <el-option label="마무리" value="FINISHING" />
              <el-option label="다운배분" value="DOWN_DISTRIBUTION" />
              <el-option label="그레이딩" value="GRADING" />
              <el-option label="외주제작" value="OUTSOURCE" />
              <el-option label="세탁" value="WASHING" />
              <el-option label="자수" value="EMBROIDERY" />
              <el-option label="프린트" value="PRINT" />
              <el-option label="검품" value="QC" />
              <el-option label="기타" value="OTHER" />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="순서" prop="sequence">
            <el-input-number v-model="form.sequence" :min="1" :max="99" />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="담당자">
            <el-select v-model="form.worker" placeholder="담당자 선택" clearable filterable>
              <el-option
                v-for="emp in employees"
                :key="emp.id"
                :label="getEmployeeLabel(emp)"
                :value="emp.id"
              />
            </el-select>
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="외주업체">
            <el-select v-model="form.supplier" placeholder="외주업체 선택" clearable filterable>
              <el-option
                v-for="supplier in suppliers"
                :key="supplier.supplier_id"
                :label="supplier.name"
                :value="supplier.supplier_id"
              />
            </el-select>
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="예정시작일">
            <el-date-picker
              v-model="form.planned_start"
              type="date"
              placeholder="시작일 선택"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="예정종료일">
            <el-date-picker
              v-model="form.planned_end"
              type="date"
              placeholder="종료일 선택"
              format="YYYY-MM-DD"
              value-format="YYYY-MM-DD"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-row :gutter="20">
        <el-col :span="12">
          <el-form-item label="인건비">
            <el-input-number
              v-model="form.labor_cost"
              :min="0"
              :precision="0"
              :step="1000"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
        <el-col :span="12">
          <el-form-item label="외주비">
            <el-input-number
              v-model="form.outsourcing_cost"
              :min="0"
              :precision="0"
              :step="1000"
              style="width: 100%;"
            />
          </el-form-item>
        </el-col>
      </el-row>

      <el-form-item label="메모">
        <el-input
          v-model="form.memo"
          type="textarea"
          :rows="2"
          placeholder="메모 입력"
        />
      </el-form-item>
    </el-form>

    <template #footer>
      <el-button @click="$emit('update:modelValue', false)">취소</el-button>
      <el-button type="primary" @click="handleSubmit" :loading="submitting">
        {{ isEditMode ? '저장' : '추가' }}
      </el-button>
    </template>
  </el-dialog>
</template>

<script setup>
import { ref, reactive, computed, watch } from 'vue'
import { ElMessage } from 'element-plus'
import axiosInstance from '@/api/axios'

const props = defineProps({
  modelValue: Boolean,
  sampleRequestId: { type: [String, Number], default: null },
  sampleRoundId: { type: [String, Number], default: null },
  editWork: { type: Object, default: null }
})

const emit = defineEmits(['update:modelValue', 'saved'])

const isEditMode = computed(() => !!props.editWork)

const formRef = ref(null)
const loading = ref(false)
const submitting = ref(false)
const employees = ref([])
const suppliers = ref([])

const form = reactive({
  work_type: null,
  sequence: 1,
  worker: null,
  supplier: null,
  planned_start: null,
  planned_end: null,
  labor_cost: 0,
  outsourcing_cost: 0,
  memo: ''
})

const getEmployeeLabel = (emp) => {
  const name = emp.get_full_name || emp.name || `${emp.last_name || ''}${emp.first_name || ''}`
  if (emp.department_name) return `${name} (${emp.department_name})`
  return name || `ID: ${emp.id}`
}

const rules = {
  work_type: [{ required: true, message: '작업유형을 선택하세요', trigger: 'change' }],
  sequence: [{ required: true, message: '순서를 입력하세요', trigger: 'blur' }]
}

const fetchEmployees = async () => {
  try {
    const response = await axiosInstance.get('employees/')
    employees.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch employees:', error)
  }
}

const fetchSuppliers = async () => {
  try {
    const response = await axiosInstance.get('suppliers/')
    suppliers.value = response.data.results || response.data
  } catch (error) {
    console.error('Failed to fetch suppliers:', error)
  }
}

const resetForm = () => {
  form.work_type = null
  form.sequence = 1
  form.worker = null
  form.supplier = null
  form.planned_start = null
  form.planned_end = null
  form.labor_cost = 0
  form.outsourcing_cost = 0
  form.memo = ''
}

const populateForm = (work) => {
  form.work_type = work.work_type
  form.sequence = work.sequence
  form.worker = work.worker
  form.supplier = work.supplier
  form.planned_start = work.planned_start
  form.planned_end = work.planned_end
  form.labor_cost = Number(work.labor_cost) || 0
  form.outsourcing_cost = Number(work.outsourcing_cost) || 0
  form.memo = work.memo || ''
}

const handleSubmit = async () => {
  if (!formRef.value) return

  try {
    await formRef.value.validate()
    submitting.value = true

    const data = {
      ...form,
      sample_request: props.sampleRequestId,
      ...(props.sampleRoundId ? { sample_round: props.sampleRoundId } : {})
    }

    if (isEditMode.value) {
      await axiosInstance.patch(`sample-works/${props.editWork.id}/`, data)
      ElMessage.success('작업이 수정되었습니다.')
    } else {
      await axiosInstance.post('sample-works/', data)
      ElMessage.success('작업이 추가되었습니다.')
    }

    emit('update:modelValue', false)
    emit('saved')
  } catch (error) {
    if (error !== 'cancel' && error !== false) {
      console.error('Submit failed:', error)
      ElMessage.error(error.response?.data?.detail || '저장 중 오류가 발생했습니다.')
    }
  } finally {
    submitting.value = false
  }
}

watch(() => props.modelValue, (newVal) => {
  if (newVal) {
    if (props.editWork) {
      populateForm(props.editWork)
    } else {
      resetForm()
    }
    fetchEmployees()
    fetchSuppliers()
  }
})
</script>

<style scoped>
.el-select {
  width: 100%;
}
</style>
